import React, { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { matchPath, useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Constants
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';

// Utils
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

//  Components
import { WpModal, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpIllustration, Illustrations } from 'Wpds/components/illustration';
import SignUpTermsAgreement from 'Shared/components/SignUpTermsAgreement/SignUpTermsAgreement';

// Styles
import './AgreeToPiTermsModal.scss';

const AgreeToPiTermsModal = () => {
  const { pathname } = useLocation();
  const { featureFlagShowPiTermsOfServiceAgreementModal } = useFlags();
  const { user, getFlag, updateFlag } = useCurrentUser();
  const [isAgreeing, toggleIsAgreeing] = useToggle(false);
  const { tr } = useTranslation();

  const flag = 'has_agreed_to_pi_terms';
  const hasAgreedToTerms = getFlag({ flag });

  const isOnboardingRoute = matchPath(pathname, { path: [PageUrlsConstants.ONBOARDING_WIZARD] });

  const handleAgreeToTerms = useCallback(() => {
    toggleIsAgreeing();
    updateFlag({ flag, value: convertMomentToUnix(getCurrentMoment()) }).then(() => {
      toggleIsAgreeing();
    });
  }, []);

  if (!featureFlagShowPiTermsOfServiceAgreementModal) return null;
  if (isOnboardingRoute) return null;
  if (isEmpty(user)) return null;
  if (hasAgreedToTerms) return null;
  // only show if onboarding is completed
  if (user && !user.onboarding?.completed_at) return null;

  const baseClassName = 'AgreeToPiTermsModal';
  return (
    <WpModal isOpen={true} size="sm">
      <WpModalBody>
        <WpIllustration className={`${baseClassName}__logo`} src={Illustrations.pi_logo_full_red_with_text} />
        <h4 className={`${baseClassName}__title`}>{tr('perform.terms_modal.title')}</h4>
        <p>{tr('perform.terms_modal.description')}</p>
        <SignUpTermsAgreement
          prefixText={tr('perform.terms_modal.agreement_prefix')}
          className={`${baseClassName}__agree-text`}
        />
        <p>
          {tr('perform.terms_modal.contact_prefix')}&nbsp;
          <a href={ExternalLinksConstants.contact_us} target="_blank" rel="noopener noreferrer">
            {tr('perform.terms_modal.contact_link')}
          </a>
          {tr('perform.terms_modal.contact_suffix')}
        </p>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton onClick={handleAgreeToTerms} loading={isAgreeing} size="lg" qaTest="accept-pi-terms-btn">
            {tr('perform.terms_modal.accept_button')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default AgreeToPiTermsModal;
